import React, { useState } from 'react';
import { AddOutlined, SearchOutlined, FileDownloadOutlined, EmailOutlined } from '@mui/icons-material';
import UsersTable    from '../../molecules/UsersTable/UsersTable';
import UserModal     from '../../molecules/UserModal/UserModal';
import Pagination    from '../../atoms/Pagination/Pagination';
import ConfirmBar    from '../../atoms/ConfirmBar/ConfirmBar';
import { exportUsersToCSV } from '../../../utils/exportUtils';
import { showToast } from '../../atoms/Toast/Toast';
import authService from '../../../services/authService';
import './UsersManagement.css';
 
const UsersManagement = ({
  usersList = [],
  adminsList = [],
  isLoading,
  onUserCreate,
  onUserUpdate,
  onUserDelete,
  onUserToggle,
  onSupervisionAdd,
  onSupervisionRemove,
  onShareHierarchy,
  onOpenBulkEmail,
  isSendingBulkEmail,
}) => {
  const [searchTerm,   setSearchTerm]   = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [onlineFilter, setOnlineFilter] = useState('all'); // 'all' | 'online' | 'offline'
  const [internalFilter, setInternalFilter] = useState('all'); // 'all' | 'internal' | 'external'
  const [currentPage,  setCurrentPage]  = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showModal,    setShowModal]    = useState(false);
  const [editingUser,  setEditingUser]  = useState(null);
 
  // ── Confirmation email après création ──
  const [pendingCreateConfirm, setPendingCreateConfirm] = useState(null);
  // { userId, username, role, adminId }
 
  const sortedUsers = [...usersList].sort((a, b) => {
    const fullNameA = `${a.name || ''} ${a.lastname || ''}`.trim();
    const fullNameB = `${b.name || ''} ${b.lastname || ''}`.trim();
    return fullNameA.localeCompare(fullNameB, 'fr', { sensitivity: 'base' });
  });
 
  const filteredUsers = sortedUsers.filter((user) => {
  const term = searchTerm.toLowerCase();
  const matchSearch =
    user.name?.toLowerCase().includes(term)         ||
    user.lastname?.toLowerCase().includes(term)     ||
    user.email?.toLowerCase().includes(term)        ||
    user.username?.toLowerCase().includes(term)     ||
    user.organization?.toLowerCase().includes(term);
  const matchStatus =
    statusFilter === 'all'      ? true :
    statusFilter === 'active'   ? user.active :
    statusFilter === 'inactive' ? !user.active : true;
  const matchOnline =
    onlineFilter === 'all'     ? true :
    onlineFilter === 'online'  ? user.is_online :
    onlineFilter === 'offline' ? !user.is_online : true;
  const matchInternal =
    internalFilter === 'all'      ? true :
    internalFilter === 'internal' ? user.is_internal :
    internalFilter === 'external' ? !user.is_internal : true;
  return matchSearch && matchStatus && matchOnline && matchInternal;
});
 
  const totalPages     = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex     = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);
 
  const totalUsers    = usersList.length;
  const activeUsers   = usersList.filter((u) => u.active).length;
  const inactiveUsers = usersList.filter((u) => !u.active).length;
  const internalUsers = usersList.filter((u) => u.is_internal).length;
  const externalUsers = usersList.filter((u) => !u.is_internal).length;
  const [pendingDeleteConfirm, setPendingDeleteConfirm] = useState(null);
  const handleOpenCreate = () => { setEditingUser(null); setShowModal(true); };
  const handleOpenEdit   = (user) => { setEditingUser(user); setShowModal(true); };
  const handleCloseModal = () => { setShowModal(false); setEditingUser(null); };
 const handleDelete = async (userId, username) => {
    const response = await onUserDelete(userId);
    if (response?.data?.pending_email_confirmation) {
        setPendingDeleteConfirm({ userId: response.data.user_id, username });
    }
};
 
const handleConfirmDeleteEmail = async () => {
    if (!pendingDeleteConfirm) return;
    try {
        await authService.confirmDeleteEmail(pendingDeleteConfirm.userId);
        showToast('Email de suppression envoyé !', 'success');
    } catch {
        showToast("Erreur lors de l'envoi de l'email.", 'error');
    } finally {
        setPendingDeleteConfirm(null);
    }
};
 
const handleCancelDeleteEmail = () => {
    setPendingDeleteConfirm(null);
    showToast('Utilisateur supprimé sans email.', 'success');
};
const [pendingToggleConfirm, setPendingToggleConfirm] = useState(null);
 
const handleToggle = async (userId, active) => {
    const response = await onUserToggle(userId, active);
    const user = usersList.find(u => String(u.id) === String(userId));
    if (response?.data?.pending_email_confirmation) {
        setPendingToggleConfirm({
            userId: response.data.user_id,
            username: user?.username || '',
            active,
        });
    }
};
 
const handleConfirmToggleEmail = async () => {
    if (!pendingToggleConfirm) return;
    try {
        await authService.confirmToggleEmail(pendingToggleConfirm.userId);
        showToast('Email de statut envoyé !', 'success');
    } catch {
        showToast("Erreur lors de l'envoi de l'email.", 'error');
    } finally {
        setPendingToggleConfirm(null);
    }
};
 
const handleCancelToggleEmail = () => {
    setPendingToggleConfirm(null);
    showToast('Statut mis à jour sans email.', 'success');
};
  const handleSubmit = async (formData, confirm = false) => {
    if (editingUser) {
      const response = await onUserUpdate(editingUser.id, formData, confirm);
      if (response?.data?.preview) return response; // UserModal gère ConfirmBar email
    } else {
      const response = await onUserCreate(formData);
      if (response?.data?.pending_email_confirmation) {
        setPendingCreateConfirm({
          userId:   response.data.user_id,
          username: response.data.username,
          role:     formData.role,
          adminId:  formData.supervised_by_admin_ids?.[0] || null,
        });
        setShowModal(false);
        return;
      }
    }
    handleCloseModal();
  };
 
  const handleConfirmCreateEmail = async () => {
    if (!pendingCreateConfirm) return;
    try {
      await authService.confirmCreateEmail(
        pendingCreateConfirm.userId,
        pendingCreateConfirm.role,
        pendingCreateConfirm.adminId,
      );
      showToast('Email de bienvenue envoyé !', 'success');
    } catch {
      showToast("Erreur lors de l'envoi de l'email.", 'error');
    } finally {
      setPendingCreateConfirm(null);
    }
  };
 
  const handleCancelCreateEmail = () => {
    setPendingCreateConfirm(null);
    showToast('Utilisateur créé sans email.', 'success');
  };
 
  const handleSearchChange = (v) => { setSearchTerm(v); setCurrentPage(1); };
  const handleFilterChange       = (f) => { setStatusFilter(f); setCurrentPage(1); };
const handleOnlineFilterChange = (f) => { setOnlineFilter(f); setCurrentPage(1); };
const handleInternalFilterChange = (f) => { setInternalFilter(f); setCurrentPage(1); };
  const handleExportCSV    = () => {
    const date = new Date().toLocaleDateString('fr-FR').replace(/\//g, '-');
    exportUsersToCSV(filteredUsers, `utilisateurs_SecuFire_Action_${date}.csv`);
  };
 
  return (
<div className="users-management">
 
      {/* ── Header ── */}
<div className="users-header">
<h2>Gestion des Utilisateurs</h2>
<div className="users-header-actions">
<button
            className="bulk-email-button"
            onClick={onOpenBulkEmail}
            disabled={isSendingBulkEmail}
>
<EmailOutlined />
            {isSendingBulkEmail ? 'Envoi…' : 'Email groupé'}
</button>
<button className="export-button" onClick={handleExportCSV}>
<FileDownloadOutlined />
            Exporter CSV
</button>
<button className="add-button" onClick={handleOpenCreate}>
<AddOutlined />
            Ajouter un utilisateur
</button>
</div>
</div>
 
      {/* ── Toolbar ── */}
<div className="users-toolbar">
  <div className="search-bar">
    <SearchOutlined />
    <input
      type="text"
      placeholder="Rechercher par nom, email ou organisation..."
      value={searchTerm}
      onChange={(e) => handleSearchChange(e.target.value)}
    />
  </div>

  <div className="filter-groups">
    <div className="filter-group">
      <span className="filter-group-label">Statut :</span>
      <div className="filter-buttons">
        {[
          { key: 'all',      label: 'Tous'     },
          { key: 'active',   label: 'Actifs'   },
          { key: 'inactive', label: 'Inactifs' },
        ].map(({ key, label }) => (
          <button
            key={key}
            className={`filter-btn ${statusFilter === key ? 'active' : ''}`}
            onClick={() => handleFilterChange(key)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>

    <div className="filter-group">
      <span className="filter-group-label">Connexion :</span>
      <div className="filter-buttons">
        {[
          { key: 'all',     label: 'Tous',       dot: null      },
          { key: 'online',  label: 'En ligne',   dot: '#22c55e' },
          { key: 'offline', label: 'Hors ligne', dot: '#9ca3af' },
        ].map(({ key, label, dot }) => (
          <button
            key={key}
            className={`filter-btn ${onlineFilter === key ? 'active' : ''}`}
            onClick={() => handleOnlineFilterChange(key)}
          >
            {dot && <span className="filter-btn-dot" style={{ background: dot }} />}
            {label}
          </button>
        ))}
      </div>
    </div>

    <div className="filter-group">
      <span className="filter-group-label">Type :</span>
      <div className="filter-buttons">
        {[
          { key: 'all',      label: 'Tous'     },
          { key: 'internal', label: 'Interne'  },
          { key: 'external', label: 'Externe'  },
        ].map(({ key, label }) => (
          <button
            key={key}
            className={`filter-btn ${internalFilter === key ? 'active' : ''}`}
            onClick={() => handleInternalFilterChange(key)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  </div>
</div>
 
      {/* ── Stats ── */}
<div className="users-stats">
<div className="stat-item">
<span className="stat-label">Total</span>
<span className="stat-value">{totalUsers}</span>
</div>
<div className="stat-item">
<span className="stat-label">Actifs</span>
<span className="stat-value active">{activeUsers}</span>
</div>
<div className="stat-item">
<span className="stat-label">Inactifs</span>
<span className="stat-value inactive">{inactiveUsers}</span>
</div>
<div className="stat-item">
<span className="stat-label">Affichés</span>
<span className="stat-value">{filteredUsers.length}</span>
</div>
<div className="stat-item">
<span className="stat-label">Internes</span>
<span className="stat-value">{internalUsers}</span>
</div>
<div className="stat-item">
<span className="stat-label">Externes</span>
<span className="stat-value">{externalUsers}</span>
</div>
</div>
 
      {/* ── Table + Pagination ── */}
<div className="users-table-wrapper">
<div className="users-table-content">
<UsersTable

    usersList={paginatedUsers}

    allUsers={usersList}

    adminsList={adminsList}

    isLoading={isLoading}

    onEdit={handleOpenEdit}

    onDelete={(userId, username) => handleDelete(userId, username)}

    onToggleStatus={handleToggle}

    onSupervisionAdd={onSupervisionAdd}

    onSupervisionRemove={onSupervisionRemove}

    onShareHierarchy={onShareHierarchy}

/>
 
</div>
<div className="users-table-pagination">
<Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredUsers.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
          />
</div>
</div>
 
      {/* ── Modal création / édition ── */}
      {showModal && (
<UserModal
          user={editingUser}
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
          adminsList={adminsList}
        />
      )}
 
      {/* ── ConfirmBar email après création ── */}
      {pendingCreateConfirm && (
<ConfirmBar
          message="Envoyer un email de bienvenue à"
          highlight={pendingCreateConfirm.username}
          onConfirm={handleConfirmCreateEmail}
          onCancel={handleCancelCreateEmail}
          confirmLabel="Oui, envoyer l'email"
          cancelLabel="Non, ne pas envoyer"
        />
      )}
      {pendingDeleteConfirm && (
<ConfirmBar
        message="Envoyer un email de suppression à"
        highlight={pendingDeleteConfirm.username}
        onConfirm={handleConfirmDeleteEmail}
        onCancel={handleCancelDeleteEmail}
        confirmLabel="Oui, envoyer l'email"
        cancelLabel="Non, ne pas envoyer"
    />
)}
{pendingToggleConfirm && (
<ConfirmBar
        message={`Envoyer un email de ${pendingToggleConfirm.active ? 'activation' : 'désactivation'} à`}
        highlight={pendingToggleConfirm.username}
        onConfirm={handleConfirmToggleEmail}
        onCancel={handleCancelToggleEmail}
        confirmLabel="Oui, envoyer l'email"
        cancelLabel="Non, ne pas envoyer"
    />
)}
</div>
  );
};
 
export default UsersManagement;